import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Mail, MessageCircle, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { Reveal } from "../components/landing/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { scrollToSection } from "../components/landing/scroll";
import { useSiteData } from "../context/SiteData";
import { useBooking } from "../context/BookingContext";
import { getSeoPage, SEO_PAGES, TRANSLATED_SLUGS } from "../data/seoPages";
import { localePrefix } from "../data/i18n";
import { optImg } from "../lib/img";
import { SeasonalBanner } from "../components/landing/SeasonalTheme";

export const SeoLandingPage = ({ lang = "en", slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const navigate = useNavigate();
  const { tours, content, settings, fmtPrice } = useSiteData();
  const { openBooking } = useBooking();

  const page = getSeoPage(slug, lang);

  if (!page || (lang !== "en" && !TRANSLATED_SLUGS.includes(slug))) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="seo-page-not-found">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-5 pt-32 pb-20 text-center">
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#0b132b]">Page not found</h1>
          <Link
            to="/"
            data-testid="seo-page-not-found-home"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-3 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to homepage
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const prefix = localePrefix(lang);
  const alternates = {
    en: `/${page.slug}`,
    ...(TRANSLATED_SLUGS.includes(page.slug)
      ? { de: `/de/${page.slug}`, fr: `/fr/${page.slug}`, ar: `/ar/${page.slug}` }
      : {}),
  };
  const htmlLang = { de: "de-CH", fr: "fr-CH", ar: "ar" }[lang] || "en";
  const whatsappNumber = (settings.whatsapp || "").replace(/[^0-9]/g, "");
  const relatedTours = tours.filter((t) => (page.relatedTours || []).includes(t.id));
  const relatedPages = (page.relatedPages || [])
    .map((s) => SEO_PAGES.find((p) => p.slug === s))
    .filter(Boolean);

  const goTours = () => {
    navigate(prefix || "/");
    setTimeout(() => scrollToSection("#tours"), 150);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: page.h1,
        description: page.metaDescription,
        url: `${window.location.origin}${prefix}/${page.slug}`,
        provider: {
          "@type": "TravelAgency",
          name: "Voyage Switzerland",
          url: window.location.origin,
        },
        areaServed: { "@type": "Country", name: "Switzerland" },
        serviceType: page.eyebrow,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${window.location.origin}${prefix || "/"}` },
          { "@type": "ListItem", position: 2, name: page.eyebrow },
        ],
      },
      ...(page.faqs?.length
        ? [
            {
              "@type": "FAQPage",
              mainEntity: page.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <div data-testid="seo-landing-page">
      <Seo
        title={page.seoTitle}
        siteName="Voyage Switzerland"
        description={page.metaDescription}
        image={page.image}
        jsonLd={jsonLd}
        alternates={alternates}
        lang={htmlLang}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-[110px] sm:pt-[130px] pb-12 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <nav aria-label="Breadcrumb" className="mb-4 text-xs font-semibold text-slate-500" data-testid="seo-page-breadcrumb">
              <Link to={prefix || "/"} className="hover:text-[var(--brand)] transition-colors" data-testid="seo-page-breadcrumb-home">
                Home
              </Link>
              <span className="mx-2 text-slate-300">/</span>
              <span className="text-slate-600">{page.eyebrow}</span>
            </nav>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--brand)]" data-testid="seo-page-eyebrow">
              {page.eyebrow} · Voyage Switzerland
            </p>
            <h1 className="mt-3 font-display font-bold tracking-tight text-4xl sm:text-5xl text-[#0b132b] leading-[1.08]" data-testid="seo-page-h1">
              {page.h1}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed" data-testid="seo-page-intro">
              {page.intro}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button
                data-testid="seo-page-book-button"
                onClick={() => openBooking({ mode: "tour" })}
                className="group inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-7 py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] hover:-translate-y-0.5"
              >
                Book Your Tour <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="seo-page-explore-button"
                onClick={goTours}
                className="inline-flex items-center gap-2 rounded-full border-2 border-slate-200 bg-white/70 text-sm font-bold px-7 py-3 text-[#0b132b] transition-all duration-300 hover:border-[var(--brand)] hover:text-[var(--brand)]"
              >
                Explore Tours
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] overflow-hidden shadow-[0_24px_60px_-24px_rgba(11,19,43,0.3)]">
              <img src={optImg(page.image, 1600)} alt={page.imageAlt} className="w-full h-[280px] sm:h-[380px] object-cover" loading="eager" />
            </div>
          </Reveal>
        </div>
      </section>
      <SeasonalBanner page="landing" />


      {/* Content sections */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {page.sections.map((s, i) => (
            <Reveal key={i} className={i ? "mt-12" : ""}>
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]" data-testid={`seo-page-h2-${i}`}>
                {s.h2}
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">{s.body}</p>
              {s.bullets && (
                <ul className="mt-5 space-y-3" data-testid={`seo-page-bullets-${i}`}>
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-3 text-base text-slate-700">
                      <span className="mt-1 w-5 h-5 shrink-0 rounded-full bg-[var(--brand-soft)] flex items-center justify-center">
                        <Check className="w-3 h-3 text-[var(--brand)]" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}

          {/* FAQs */}
          {page.faqs?.length > 0 && (
            <Reveal className="mt-14">
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]" data-testid="seo-page-faq-title">
                Frequently asked questions
              </h2>
              <div className="mt-5 rounded-3xl bg-white border border-slate-100 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)] px-6" data-testid="seo-page-faqs">
                <Accordion type="single" collapsible>
                  {page.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-slate-100">
                      <AccordionTrigger
                        className="text-base font-bold text-[#0b132b] hover:text-[var(--brand)] hover:no-underline"
                        data-testid={`seo-page-faq-question-${i}`}
                      >
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 leading-relaxed" data-testid={`seo-page-faq-answer-${i}`}>
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </Reveal>
          )}

          {/* Contact strip */}
          <Reveal className="mt-14">
            <div className="rounded-3xl bg-[#0b132b] p-7 sm:p-9 text-white" data-testid="seo-page-contact-strip">
              <h2 className="font-display font-bold text-xl sm:text-2xl">Plan this journey with us</h2>
              <p className="mt-2 text-sm text-slate-300">
                Tell us your dates and wishes — we reply personally, usually within 24 hours.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  data-testid="seo-page-call-button"
                  href={`tel:${content.contact_phone}`}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 transition-colors hover:bg-[var(--brand-dark)]"
                >
                  <Phone className="w-4 h-4" /> {content.contact_phone}
                </a>
                {whatsappNumber && (
                  <a
                    data-testid="seo-page-whatsapp-button"
                    href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hello! I'm interested in: ${page.h1}`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white text-sm font-bold px-5 py-2.5 transition-colors hover:bg-[#1da851]"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                )}
                <a
                  data-testid="seo-page-email-button"
                  href={`mailto:${content.contact_email}?subject=${encodeURIComponent(page.h1)}`}
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white text-sm font-bold px-5 py-2.5 transition-colors hover:bg-white/20"
                >
                  <Mail className="w-4 h-4" /> {content.contact_email}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Related tours */}
      {relatedTours.length > 0 && (
        <section className="py-16 bg-[#f8fafc] border-t border-slate-100" data-testid="seo-page-tours">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]">Tours for this journey</h2>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedTours.map((t) => (
                <Reveal key={t.id}>
                  <Link
                    to={`/tours/${t.slug || t.id}`}
                    data-testid={`seo-page-tour-${t.id}`}
                    className="group block rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)] hover:shadow-[0_30px_60px_-24px_rgba(225,29,72,0.35)] hover:border-[var(--brand)]/25 transition-all duration-300 hover:-translate-y-1.5"
                  >
                    <div className="h-44 overflow-hidden">
                      <img src={optImg(t.image, 800)} alt={`${t.name} — ${t.destination}, Switzerland`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-5">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t.destination}</p>
                      <h3 className="mt-1 font-display font-bold text-lg text-[#0b132b] leading-tight group-hover:text-[var(--brand)] transition-colors">{t.name}</h3>
                      <p className="mt-2 text-sm font-bold text-[var(--brand)]">
                        from {fmtPrice(t.sale_price || t.price)}
                      </p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related pages — internal linking */}
      {relatedPages.length > 0 && (
        <section className="py-14 bg-white border-t border-slate-100" data-testid="seo-page-related">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <Reveal>
              <h2 className="font-display font-bold text-xl sm:text-2xl text-[#0b132b]">Keep exploring</h2>
              <ul className="mt-5 space-y-3">
                {relatedPages.map((p) => (
                  <li key={p.slug}>
                    <Link
                      to={`${prefix}/${p.slug}`}
                      data-testid={`seo-page-related-${p.slug}`}
                      className="group flex items-center justify-between rounded-2xl border border-slate-100 px-5 py-4 transition-all duration-300 hover:border-[var(--brand)]/30 hover:shadow-[0_12px_30px_-16px_rgba(225,29,72,0.3)]"
                    >
                      <span className="font-bold text-[#0b132b] group-hover:text-[var(--brand)] transition-colors">{p.h1}</span>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[var(--brand)] group-hover:translate-x-1 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};
